import { useMemo } from "react";

import { useFragment } from "@apollo/client";

import { useFeatureGates } from "../../../cof-feature-gates/hooks";
import { CofConfigId, type LensSpace, ResourceKind } from "../../../gql-generated/types";
import { lensSpace } from "../../api/fragments";
import { type LensFolderRoleTypePermissions, type ROLE_ACTIONS } from "../../constants";
import { type ApolloResult } from "../../models";
import { useOrgChildResources } from "../useOrgChildResources";
import { useContainersIndexV2 } from "../useOrganizationsV2";

import { getLensFolderPermissions } from "./getLensFolderPermissions";
import { getLensFolderPermissionsWithContainer } from "./getLensFolderPermissionsV2";
import { useRolesStore } from "./useRolesStore";

export const useLensFolderPermissions = (
    lensFolderId: string,
): ApolloResult<{
    lensFolderPermissions: LensFolderRoleTypePermissions;
}> => {
    const { configs } = useFeatureGates();
    const isMyLensesOrgsV2Enabled = configs[CofConfigId.MyLensesOrgsV2].enabled;

    const { data } = useFragment<LensSpace>({
        fragment: lensSpace,
        fragmentName: "LensSpaceAll",
        from: {
            __typename: "LensSpace",
            id: lensFolderId,
        },
    });

    const { rolesStore, loading: v1Loading, error: v1Error } = useRolesStore();
    const containersIndexV2 = useContainersIndexV2();

    const {
        resourceContainers: lensFolders,
        loading: v2Loading,
        error: v2Error,
    } = useOrgChildResources({
        kind: ResourceKind.LensSpaces,
    });

    const lensFolderPermissions = useMemo(() => {
        if (isMyLensesOrgsV2Enabled) {
            const lensFolderContainer = lensFolders.find((lf) => lf.id === lensFolderId) ?? null;
            return getLensFolderPermissionsWithContainer(lensFolderContainer, containersIndexV2);
        }
        return getLensFolderPermissions(data.organizationId ?? "", lensFolderId, rolesStore);
    }, [isMyLensesOrgsV2Enabled, lensFolderId, lensFolders, containersIndexV2, data, rolesStore]);

    const loading = isMyLensesOrgsV2Enabled ? v2Loading : v1Loading;
    const error = isMyLensesOrgsV2Enabled ? v2Error : v1Error;

    return {
        lensFolderPermissions,
        loading,
        error,
    };
};

export const usePermittedBasedOnLensFolderRoleAction = (
    lensFolderId: string,
    roleAction: ROLE_ACTIONS,
): ApolloResult<{ isPermitted: boolean }> => {
    const { lensFolderPermissions, loading, error } = useLensFolderPermissions(lensFolderId);
    const isPermitted = lensFolderPermissions.ACTIONS.includes(roleAction);
    return { isPermitted, loading, error };
};
